import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePageMeta, useItemListSchema } from "@/lib/seo";
import { CATEGORY_COPY } from "@/constants/categoryCopy";
import { SERVICE_AREA_SHORT } from "@/constants/store";
import ProductCard from "@/components/shop/ProductCard";
import { DepartmentPolicy } from "@/components/policy/DepartmentPolicy";
import { Search } from "lucide-react";

export default function CatalogPage() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 60;
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const sort = searchParams.get("sort") || "newest";

  useEffect(() => {
    setSearch(searchParams.get("q") || "");
  }, [searchParams]);

  const avail = searchParams.get("avail") || "all";

  const activeCategory = useMemo(
    () => category || searchParams.get("category") || "all",
    [category, searchParams]
  );

  usePageMeta(
    activeCategory === "all" ? "Shop All Products" : `${activeCategory} for Sale — ${SERVICE_AREA_SHORT}`,
    activeCategory === "all"
      ? "Shop closeout & overstock furniture, appliances, e-bikes, tools and liquidation pallets at IPVALUE MARKETPLACE — brand-new merchandise at closeout prices in Grand Rapids, MI."
      : `Discount ${activeCategory.toLowerCase()} in Grand Rapids, MI — brand-new overstock and closeout ${activeCategory.toLowerCase()} at IPVALUE MARKETPLACE. Free same-day pickup or local delivery across West Michigan.`
  );
  useItemListSchema(activeCategory === "all" ? "Shop All" : activeCategory, products);

  useEffect(() => {
    api.get("/products/categories").then((r) => setCategories(r.data.categories));
  }, []);

  useEffect(() => {
    setPage(0);
  }, [activeCategory, sort, search, avail]);

  const reqRef = useRef(0);
  useEffect(() => {
    const rid = ++reqRef.current;
    const run = () => {
      if (page === 0) setLoading(true);
      const params = { sort, limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (activeCategory !== "all") params.category = activeCategory;
      if (search) params.search = search;
      if (avail === "today" || avail === "instore") { params.fulfillment = "in_stock"; params.badge = "in_store"; }
      if (avail === "ships") params.badge = "ships";
      if (avail === "order") params.fulfillment = "order_in";
      api
        .get("/products", { params })
        .then((r) => {
          if (reqRef.current !== rid) return; // stale response — a newer filter/search superseded it
          setTotal(r.data.total || 0);
          setProducts((prev) => (page === 0 ? r.data.items : [...prev, ...r.data.items]));
        })
        .finally(() => {
          if (reqRef.current === rid) setLoading(false);
        });
    };
    const t = setTimeout(run, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [activeCategory, sort, search, avail, page]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-12 lg:py-16" data-testid="catalog-page">
      <div className="ipv-eyebrow text-[#0A1C40]">The Shop</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3">
        {activeCategory === "all" ? "All products" : activeCategory}
      </h1>
      <p className="text-gray-600 mt-3 max-w-2xl" data-testid="catalog-total-count">
        {total} {avail === "order" ? "special-order item" : avail === "ships" ? "ships-to-you item" : avail === "today" || avail === "instore" ? "in-store item" : "item"}{total !== 1 ? "s" : ""}{avail === "ships" ? " — delivered by carrier" : " — in store and online"}.
      </p>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="availability-tabs">
        {[
          { id: "all", label: "All Items" },
          { id: "instore", label: "Available in store" },
          { id: "ships", label: "Ships to you" },
          { id: "order", label: "Special order" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              const sp = new URLSearchParams(searchParams);
              if (t.id === "all") sp.delete("avail");
              else sp.set("avail", t.id);
              setSearchParams(sp);
            }}
            className={`px-4 py-2 border-2 rounded-full text-sm font-bold [touch-action:manipulation] ${
              avail === t.id
                ? "border-[#0A1C40] bg-[#0A1C40] text-white"
                : "border-gray-300 text-gray-700 hover:border-[#0A1C40]"
            }`}
            data-testid={`avail-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-10">
        {/* Mobile controls — compact bar instead of the tall sidebar */}
        <div className="lg:hidden space-y-3 min-w-0" data-testid="catalog-mobile-controls">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className="input-base pl-9"
                placeholder="Search products…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="catalog-search-input-mobile"
              />
            </div>
            <select
              className="input-base max-w-[130px] shrink-0"
              value={sort}
              onChange={(e) => {
                const sp = new URLSearchParams(searchParams);
                sp.set("sort", e.target.value);
                setSearchParams(sp);
              }}
              data-testid="catalog-sort-select-mobile"
            >
              <option value="newest">Newest</option>
              <option value="price_asc">Price ↑</option>
              <option value="price_desc">Price ↓</option>
              <option value="name">Name A→Z</option>
            </select>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]" data-testid="catalog-category-chips">
            <a
              href="/shop"
              className={`shrink-0 px-3.5 py-1.5 rounded-full border-2 text-sm font-bold whitespace-nowrap ${
                activeCategory === "all" ? "border-[#0A1C40] bg-[#0A1C40] text-white" : "border-gray-300 text-gray-700"
              }`}
            >
              All
            </a>
            {categories.map((c) => (
              <a
                key={c}
                href={`/shop/${c}`}
                className={`shrink-0 px-3.5 py-1.5 rounded-full border-2 text-sm font-bold whitespace-nowrap ${
                  activeCategory === c ? "border-[#0A1C40] bg-[#0A1C40] text-white" : "border-gray-300 text-gray-700"
                }`}
              >
                {c}
              </a>
            ))}
          </div>
        </div>

        <aside className="hidden lg:block space-y-8" data-testid="catalog-sidebar">
          <div>
            <div className="ipv-eyebrow mb-4">Search</div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className="input-base pl-9"
                placeholder="Search products…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="catalog-search-input"
              />
            </div>
          </div>
          <div>
            <div className="ipv-eyebrow mb-4">Categories</div>
            <div className="space-y-1">
              <a
                href="/shop"
                className={`block text-left w-full py-1.5 text-sm border-l-2 pl-3 ${
                  activeCategory === "all"
                    ? "border-[#0A1C40] text-[#0A1C40] font-bold"
                    : "border-transparent hover:border-gray-300"
                }`}
                data-testid="cat-all"
              >
                All products
              </a>
              {categories.map((c) => (
                <a
                  key={c}
                  href={`/shop/${c}`}
                  className={`block text-left w-full py-1.5 text-sm border-l-2 pl-3 ${
                    activeCategory === c
                      ? "border-[#0A1C40] text-[#0A1C40] font-bold"
                      : "border-transparent hover:border-gray-300"
                  }`}
                  data-testid={`cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c}
                </a>
              ))}
            </div>
          </div>
          <div>
            <div className="ipv-eyebrow mb-4">Sort</div>
            <select
              className="input-base"
              value={sort}
              onChange={(e) => {
                const sp = new URLSearchParams(searchParams);
                sp.set("sort", e.target.value);
                setSearchParams(sp);
              }}
              data-testid="catalog-sort-select"
            >
              <option value="newest">Newest</option>
              <option value="price_asc">Price ↑</option>
              <option value="price_desc">Price ↓</option>
              <option value="name">Name A→Z</option>
            </select>
          </div>
        </aside>

        <div className="min-w-0">
          {loading ? (
            <div className="text-center py-20 ipv-eyebrow">Loading...</div>
          ) : products.length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded-lg p-20 text-center">
              <div className="font-display font-bold text-xl">No products found</div>
              <p className="text-gray-500 mt-2">
                Try a different category or search term.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
              {products.length < total && (
                <div className="text-center mt-10">
                  <button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    className="btn-outline"
                    data-testid="catalog-load-more-btn"
                  >
                    Load more ({total - products.length} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Department policy — spec 8B: visible on every category page */}
      {activeCategory !== "all" && <DepartmentPolicy department={activeCategory} />}

      {/* §7 SEO — unique category copy, visible below the grid */}
      {CATEGORY_COPY[activeCategory] && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-8 pb-14" data-testid="category-seo-copy">
          <div className="border-t-2 border-[#FFC220] pt-8 max-w-3xl">
            <h2 className="font-display font-black text-lg text-[#0A1C40]">
              About {activeCategory} at IPVALUE MARKETPLACE
            </h2>
            <p className="mt-3 text-sm text-gray-600 leading-relaxed">
              {CATEGORY_COPY[activeCategory]}
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
